import React, { useState, useEffect } from "react";
import { CustomCheckBox } from "../../../components";
import { useForm } from "react-hook-form";
import { PeopleFill, CarFrontFill, Cash, Speedometer, FunnelFill, ChevronDown, ChevronUp, TagFill, FuelPumpFill, GearFill, Infinity, Search, ArrowCounterclockwise } from "react-bootstrap-icons";
import axiosInstance from "../../../services/axios.instance";
import { API_URL } from "../../../services/apiConfig";

interface CreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCustomizeResult: (result: any) => void;
  initialValues?: any;
  isInline?: boolean;
  filterCatalog?: {
    brands: string[];
    fuelTypes: string[];
    transmissions: string[];
    minPrice: number;
    maxPrice: number;
    minWeeklyPrice: number;
    maxWeeklyPrice: number;
    minMonthlyPrice: number;
    maxMonthlyPrice: number;
    carTypes: string[];
  };
  totalCars?: any[];
}

const CustomerCustomizeYourSearch: React.FC<CreateModalProps> = ({
  isOpen,
  onClose,
  onCustomizeResult,
  initialValues,
  isInline = false,
  filterCatalog,
  totalCars = [],
}) => {
  const {
    formState: { },
  } = useForm({ mode: "onChange" });

  const [priceRange, setPriceRange] = useState({ min: 0, max: 0 });
  const [weeklyPriceRange, setWeeklyPriceRange] = useState({ min: 0, max: 0 });
  const [monthlyPriceRange, setMonthlyPriceRange] = useState({ min: 0, max: 0 });
  const [mileageValue, SetMileageValue] = useState<string | number>("");
  const [selectedSeatCapacity, setSelectedSeatCapacity] = useState<number[]>([]);
  const [selectedCarTypes, setSelectedCarTypes] = useState<string[]>([]);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [selectedFuelTypes, setSelectedFuelTypes] = useState<string[]>([]);
  const [selectedTransmissions, setSelectedTransmissions] = useState<string[]>([]);
  const [isUnlimitedMileage, setIsUnlimitedMileage] = useState(false);
  const [brandSearchQuery, setBrandSearchQuery] = useState("");
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [carTypes, setCarTypes] = useState<string[]>([]);
  const [carTypesLoading, setCarTypesLoading] = useState<boolean>(false);
  const [hoveredType, setHoveredType] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);

  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
    seatCapacity: false,
    carTypes: false,
    brands: false,
    fuelTypes: true,
    transmissions: true,
    budget: false,
    mileage: false,
  });

  const normalizedCarTypes = React.useMemo(() => {
    const list = filterCatalog?.carTypes || carTypes || [];
    return Array.from(new Set(list.map((v: any) => String(v || "").toUpperCase()))).sort();
  }, [filterCatalog?.carTypes, carTypes]);

  const normalizedBrands = React.useMemo(() => {
    const list = filterCatalog?.brands || [];
    return Array.from(new Set(list.map((v: any) => String(v || "").toUpperCase()))).sort();
  }, [filterCatalog?.brands]);

  const normalizedFuelTypes = React.useMemo(() => {
    const list = filterCatalog?.fuelTypes || [];
    return Array.from(new Set(list.map((v: any) => String(v || "").toUpperCase()))).sort();
  }, [filterCatalog?.fuelTypes]);

  const normalizedTransmissions = React.useMemo(() => {
    const list = filterCatalog?.transmissions || [];
    return Array.from(new Set(list.map((v: any) => String(v || "").toUpperCase()))).sort();
  }, [filterCatalog?.transmissions]);

  const isSelected = (value: string, selectedList: string[]) => {
    return selectedList.some(item => String(item || "").toUpperCase() === String(value || "").toUpperCase());
  };

  const toggleNormalizedValue = (value: string, setter: React.Dispatch<React.SetStateAction<string[]>>) => {
    setter((prev: string[]) => {
      const upperValue = String(value || "").toUpperCase();
      const exists = prev.some(item => String(item || "").toUpperCase() === upperValue);
      if (exists) {
        return prev.filter(item => String(item || "").toUpperCase() !== upperValue);
      } else {
        return [...prev, upperValue];
      }
    });
  };

  const toggleSection = (section: string) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleDailyPriceChange = (min: number, max: number) => {
    setPriceRange({ min, max });
    setWeeklyPriceRange({
      min: filterCatalog?.minWeeklyPrice || 0,
      max: filterCatalog?.maxWeeklyPrice || 0,
    });
    setMonthlyPriceRange({
      min: filterCatalog?.minMonthlyPrice || 0,
      max: filterCatalog?.maxMonthlyPrice || 0,
    });
  };

  const handleWeeklyPriceChange = (min: number, max: number) => {
    setWeeklyPriceRange({ min, max });
    setPriceRange({
      min: filterCatalog?.minPrice || 0,
      max: filterCatalog?.maxPrice || 0,
    });
    setMonthlyPriceRange({
      min: filterCatalog?.minMonthlyPrice || 0,
      max: filterCatalog?.maxMonthlyPrice || 0,
    });
  };

  const handleMonthlyPriceChange = (min: number, max: number) => {
    setMonthlyPriceRange({ min, max });
    setPriceRange({
      min: filterCatalog?.minPrice || 0,
      max: filterCatalog?.maxPrice || 0,
    });
    setWeeklyPriceRange({
      min: filterCatalog?.minWeeklyPrice || 0,
      max: filterCatalog?.maxWeeklyPrice || 0,
    });
  };

  // Fetch car types from API on mount
  useEffect(() => {
    const fetchCarTypes = async () => {
      setCarTypesLoading(true);
      try {
        const response = await axiosInstance.get(API_URL.CAR_TYPES);
        if (Array.isArray(response.data)) {
          setCarTypes(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch car types:", error);
      } finally {
        setCarTypesLoading(false);
      }
    };
    fetchCarTypes();
  }, []);

  // Restore previous customization values when modal opens
  useEffect(() => {
    if (initialValues) {
      if (initialValues.seat_capacity) setSelectedSeatCapacity(Array.isArray(initialValues.seat_capacity) ? initialValues.seat_capacity : [initialValues.seat_capacity]);
      if (initialValues.priceRange) setPriceRange(initialValues.priceRange);
      if (initialValues.weeklyPriceRange) setWeeklyPriceRange(initialValues.weeklyPriceRange);
      if (initialValues.monthlyPriceRange) setMonthlyPriceRange(initialValues.monthlyPriceRange);
      if (initialValues.selectedCarTypes) setSelectedCarTypes(initialValues.selectedCarTypes);
      if (initialValues.selectedBrands) setSelectedBrands(initialValues.selectedBrands);
      if (initialValues.selectedFuelTypes) setSelectedFuelTypes(initialValues.selectedFuelTypes);
      if (initialValues.selectedTransmissions) setSelectedTransmissions(initialValues.selectedTransmissions);
      if (initialValues.isUnlimitedMileage !== undefined) setIsUnlimitedMileage(initialValues.isUnlimitedMileage);
      if (initialValues.mileage) SetMileageValue(initialValues.mileage);
      if (initialValues.model_year) setSelectedYear(initialValues.model_year);

      // Backward compatibility
      if (initialValues.amount_range && !initialValues.priceRange) {
        setPriceRange(prev => ({ ...prev, max: initialValues.amount_range }));
      }
      if (initialValues.car_type && !initialValues.selectedCarTypes) {
        setSelectedCarTypes([initialValues.car_type]);
      }
    }
  }, [initialValues, isOpen]);

  // Automatically trigger customization on value changes for inline view
  useEffect(() => {
    if (isInline) {
      const timer = setTimeout(() => {
        const customizeResult = {
          seat_capacity: selectedSeatCapacity.length > 0 ? selectedSeatCapacity : null,
          priceRange,
          weeklyPriceRange,
          monthlyPriceRange,
          car_type: selectedCarTypes[0] || "", // Keep for backward compatibility
          selectedCarTypes,
          selectedBrands,
          selectedFuelTypes,
          selectedTransmissions,
          isUnlimitedMileage,
          amount_range: priceRange.max, // Keep for backward compatibility
          mileage: mileageValue,
          product_name: selectedYear,
        };
        onCustomizeResult(customizeResult);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [selectedSeatCapacity, selectedCarTypes, selectedBrands, selectedFuelTypes, selectedTransmissions, isUnlimitedMileage, priceRange, weeklyPriceRange, monthlyPriceRange, mileageValue, selectedYear, isInline]);

  const handleChangeMileage = (event: any) => {
    const value = event.target.value;
    SetMileageValue(value === "" ? "" : parseFloat(value));
  };

  const handleChangeYear = (event: any) => {
    setSelectedYear(event.target.value);
  };

  const handleCheckBoxSeatChange = (fieldName: string, value: number) => {
    if (fieldName.startsWith("Seat")) {
      setSelectedSeatCapacity(prev =>
        prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
      );
    }
  };

  const toggleValue = (value: string, setter: React.Dispatch<React.SetStateAction<string[]>>) => {
    setter((prev: string[]) =>
      prev.includes(value) ? prev.filter((i: string) => i !== value) : [...prev, value]
    );
  };

  const handleClearSearch = () => {
    setIsResetting(true);
    setSelectedSeatCapacity([]);
    setSelectedCarTypes([]);
    setSelectedBrands([]);
    setSelectedFuelTypes([]);
    setSelectedTransmissions([]);
    setIsUnlimitedMileage(false);
    setPriceRange({ min: 0, max: 0 });
    setWeeklyPriceRange({ min: 0, max: 0 });
    setMonthlyPriceRange({ min: 0, max: 0 });
    SetMileageValue("");
    setSelectedYear("");
    setTimeout(() => setIsResetting(false), 600);
  };

  const getFilteredCount = () => {
    if (!totalCars?.length) return 0;

    return totalCars.filter((car: any) => {
      const details = car?.product_details_car || {};
      const carPrice = Number(details.price_per_day) || 0;
      const normalize = (v: any) => String(v ?? "").trim().toLowerCase();
      const carType = normalize(details.car_type || details.category);
      const carBrand = normalize(details.car_brand || car.brand || car.product_name || details.car_model);
      const fuelType = normalize(details.fuel_type);
      const transmission = normalize(details.transmission);
      const seats = Number(details.number_of_seats) || 0;

      if (priceRange.min > 0 && carPrice < priceRange.min) return false;
      if (priceRange.max > 0 && carPrice > priceRange.max) return false;

      const carWeeklyPrice = Number(details.price_per_week) || 0;
      if (weeklyPriceRange.min > 0 && carWeeklyPrice < weeklyPriceRange.min) return false;
      if (weeklyPriceRange.max > 0 && carWeeklyPrice > weeklyPriceRange.max) return false;

      const carMonthlyPrice = Number(details.price_per_month) || 0;
      if (monthlyPriceRange.min > 0 && carMonthlyPrice < monthlyPriceRange.min) return false;
      if (monthlyPriceRange.max > 0 && carMonthlyPrice > monthlyPriceRange.max) return false;

      if (selectedCarTypes.length > 0 && !selectedCarTypes.map(normalize).includes(carType)) return false;
      if (selectedBrands.length > 0 && !selectedBrands.map(normalize).includes(carBrand)) return false;
      if (selectedFuelTypes.length > 0 && !selectedFuelTypes.map(normalize).includes(fuelType)) return false;
      if (selectedTransmissions.length > 0 && !selectedTransmissions.map(normalize).includes(transmission)) return false;
      if (selectedSeatCapacity.length > 0 && !selectedSeatCapacity.includes(seats)) return false;

      return true;
    }).length;
  };

  const handleProceedToCustomize = () => {
    const customizeResult = {
      seat_capacity: selectedSeatCapacity.length > 0 ? selectedSeatCapacity : null,
      priceRange,
      weeklyPriceRange,
      monthlyPriceRange,
      selectedCarTypes,
      selectedBrands,
      selectedFuelTypes,
      selectedTransmissions,
      isUnlimitedMileage,
      amount_range: priceRange.max,
      car_type: selectedCarTypes[0] || "",
      mileage: mileageValue,
      product_name: selectedYear,
    };
    onCustomizeResult(customizeResult);
    onClose();
  };

  if (isInline) {
    return (
      <div className="p-3" style={{
        textAlign: "left",
        fontFamily: "'HarmonyOS Sans', sans-serif"
      }}>
        <style>{`
          @keyframes spin-reset {
            from { transform: rotate(0deg); }
            to { transform: rotate(-360deg); }
          }
          .reset-spin {
            animation: spin-reset 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          }
        `}</style>
        <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom border-secondary" style={{ borderColor: "rgba(255,255,255,0.1) !important" }}>
          <div className="d-flex align-items-center gap-2">
            <FunnelFill style={{ color: "#F0B64F" }} size={16} />
            <div className="mb-0 fw-bold" style={{ fontSize: "15px", color: "#F0B64F", letterSpacing: "0.5px" }}>
              FILTERS
            </div>
          </div>
          <button
            onClick={handleClearSearch}
            className="btn d-flex align-items-center gap-1 border-0"
            style={{
              fontSize: "11px",
              color: "#F0B64F",
              background: "rgba(240, 182, 79, 0.1)",
              padding: "4px 10px",
              borderRadius: "8px",
              transition: "all 0.3s ease",
              fontWeight: "600",
              textTransform: "uppercase",
              letterSpacing: "0.5px"
            }}
          >
            <ArrowCounterclockwise
              size={12}
              className={isResetting ? "reset-spin" : ""}
            />
            Reset
          </button>
        </div>

        {/* Seat Capacity - Modern Chips */}
        <div className="mb-3">
          <div
            className="d-flex align-items-center justify-content-between mb-2 cursor-pointer"
            onClick={() => toggleSection("seatCapacity")}
            style={{ cursor: "pointer" }}
          >
            <div className="d-flex align-items-center gap-2" style={{ fontWeight: "600", fontSize: "11px", color: "rgba(255,255,255,0.9)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              <PeopleFill size={12} style={{ color: "#F0B64F" }} />
              <span>Seat Capacity</span>
            </div>
            {collapsedSections.seatCapacity ? <ChevronDown size={12} color="rgba(255,255,255,0.3)" /> : <ChevronUp size={12} color="rgba(255,255,255,0.3)" />}
          </div>
          {!collapsedSections.seatCapacity && (
            <div className="d-flex flex-wrap gap-1">
              {[4, 5, 7].map((seat) => (
                <div
                  key={seat}
                  onClick={() => handleCheckBoxSeatChange("Seat", seat)}
                  style={{
                    padding: "6px 12px",
                    borderRadius: "10px",
                    background: selectedSeatCapacity.includes(seat) ? "#F0B64F" : "rgba(255,255,255,0.05)",
                    color: selectedSeatCapacity.includes(seat) ? "#000" : "rgba(255,255,255,0.7)",
                    border: "1px solid",
                    borderColor: selectedSeatCapacity.includes(seat) ? "#F0B64F" : "rgba(255,255,255,0.1)",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: "600",
                    transition: "all 0.2s ease",
                    minWidth: "50px",
                    textAlign: "center"
                  }}
                >
                  {seat}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Car Types */}
        <div className="mb-3">
          <div
            className="d-flex align-items-center justify-content-between mb-2 cursor-pointer"
            onClick={() => toggleSection("carTypes")}
            style={{ cursor: "pointer" }}
          >
            <div className="d-flex align-items-center gap-2" style={{ fontWeight: "600", fontSize: "11px", color: "rgba(255,255,255,0.9)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              <CarFrontFill size={12} style={{ color: "#F0B64F" }} />
              <span>Car Types</span>
            </div>
            {collapsedSections.carTypes ? <ChevronDown size={12} color="rgba(255,255,255,0.3)" /> : <ChevronUp size={12} color="rgba(255,255,255,0.3)" />}
          </div>
          {!collapsedSections.carTypes && (
            <div className="custom-scrollbar pr-1">
              {normalizedCarTypes.map((type: string) => (
                <div
                  key={type}
                  onClick={() => toggleNormalizedValue(type, setSelectedCarTypes)}
                  className="d-flex align-items-center p-1 px-2 mb-1 rounded-3"
                  style={{
                    background: isSelected(type, selectedCarTypes) ? "rgba(240, 182, 79, 0.1)" : "rgba(255,255,255,0.02)",
                    border: "1px solid",
                    borderColor: isSelected(type, selectedCarTypes) ? "#F0B64F" : "rgba(255,255,255,0.05)",
                    cursor: "pointer",
                    transition: "all 0.2s ease"
                  }}
                >
                  <div style={{ pointerEvents: "none" }}>
                    <CustomCheckBox isChecked={isSelected(type, selectedCarTypes)} onChange={() => { }} />
                  </div>
                  <div className="ms-2 flex-grow-1 min-width-0">
                    <div style={{
                      fontSize: "12px",
                      color: isSelected(type, selectedCarTypes) ? "#F0B64F" : "rgba(255,255,255,0.8)",
                      fontWeight: isSelected(type, selectedCarTypes) ? "600" : "400",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}>
                      {type.toUpperCase()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Brands */}
        <div className="mb-3">
          <div
            className="d-flex align-items-center justify-content-between mb-2 cursor-pointer"
            onClick={() => toggleSection("brands")}
            style={{ cursor: "pointer" }}
          >
            <div className="d-flex align-items-center gap-2" style={{ fontWeight: "600", fontSize: "11px", color: "rgba(255,255,255,0.9)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              <TagFill size={12} style={{ color: "#F0B64F" }} />
              <span>Brands</span>
            </div>
            {collapsedSections.brands ? <ChevronDown size={12} color="rgba(255,255,255,0.3)" /> : <ChevronUp size={12} color="rgba(255,255,255,0.3)" />}
          </div>
          {!collapsedSections.brands && (
            <div className="px-1">
              <div className="position-relative mb-2">
                <Search size={10} style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.3)" }} />
                <input
                  type="text"
                  placeholder="Search brand..."
                  value={brandSearchQuery}
                  onChange={(e) => setBrandSearchQuery(e.target.value)}
                  style={{
                    width: "100%",
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: "8px",
                    padding: "4px 8px 4px 28px",
                    fontSize: "11px",
                    color: "#fff",
                    outline: "none"
                  }}
                />
              </div>
              <div className="custom-scrollbar pr-1" >
                {normalizedBrands.filter((b: string) => b.toLowerCase().includes(brandSearchQuery.toLowerCase())).map((brand: string) => (
                  <div
                    key={brand}
                    onClick={() => toggleNormalizedValue(brand, setSelectedBrands)}
                    className="d-flex align-items-center p-1 px-2 mb-1 rounded-3"
                    style={{
                      background: isSelected(brand, selectedBrands) ? "rgba(240, 182, 79, 0.1)" : "rgba(255,255,255,0.02)",
                      border: "1px solid",
                      borderColor: isSelected(brand, selectedBrands) ? "#F0B64F" : "rgba(255,255,255,0.05)",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ pointerEvents: "none" }}>
                      <CustomCheckBox isChecked={isSelected(brand, selectedBrands)} onChange={() => { }} />
                    </div>
                    <span className="ms-2" style={{ fontSize: "12px", color: isSelected(brand, selectedBrands) ? "#F0B64F" : "rgba(255,255,255,0.8)" }}>{brand.toUpperCase()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Fuel Types */}
        <div className="mb-3">
          <div
            className="d-flex align-items-center justify-content-between mb-2 cursor-pointer"
            onClick={() => toggleSection("fuelTypes")}
            style={{ cursor: "pointer" }}
          >
            <div className="d-flex align-items-center gap-2" style={{ fontWeight: "600", fontSize: "11px", color: "rgba(255,255,255,0.9)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              <FuelPumpFill size={12} style={{ color: "#F0B64F" }} />
              <span>Fuel Type</span>
            </div>
            {collapsedSections.fuelTypes ? <ChevronDown size={12} color="rgba(255,255,255,0.3)" /> : <ChevronUp size={12} color="rgba(255,255,255,0.3)" />}
          </div>
          {!collapsedSections.fuelTypes && (
            <div className="d-flex flex-wrap gap-1">
              {normalizedFuelTypes.map((fuel: string) => (
                <div
                  key={fuel}
                  onClick={() => toggleNormalizedValue(fuel, setSelectedFuelTypes)}
                  style={{
                    padding: "4px 10px",
                    borderRadius: "8px",
                    background: isSelected(fuel, selectedFuelTypes) ? "#F0B64F" : "rgba(255,255,255,0.05)",
                    color: isSelected(fuel, selectedFuelTypes) ? "#000" : "rgba(255,255,255,0.7)",
                    border: "1px solid",
                    borderColor: isSelected(fuel, selectedFuelTypes) ? "#F0B64F" : "rgba(255,255,255,0.1)",
                    cursor: "pointer",
                    fontSize: "11px",
                    fontWeight: "600",
                  }}
                >
                  {fuel.toUpperCase()}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Transmissions */}
        <div className="mb-3">
          <div
            className="d-flex align-items-center justify-content-between mb-2 cursor-pointer"
            onClick={() => toggleSection("transmissions")}
            style={{ cursor: "pointer" }}
          >
            <div className="d-flex align-items-center gap-2" style={{ fontWeight: "600", fontSize: "11px", color: "rgba(255,255,255,0.9)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              <GearFill size={12} style={{ color: "#F0B64F" }} />
              <span>Transmission</span>
            </div>
            {collapsedSections.transmissions ? <ChevronDown size={12} color="rgba(255,255,255,0.3)" /> : <ChevronUp size={12} color="rgba(255,255,255,0.3)" />}
          </div>
          {!collapsedSections.transmissions && (
            <div className="d-flex flex-wrap gap-1">
              {normalizedTransmissions.map((trans: string) => (
                <div
                  key={trans}
                  onClick={() => toggleNormalizedValue(trans, setSelectedTransmissions)}
                  style={{
                    padding: "4px 10px",
                    borderRadius: "8px",
                    background: isSelected(trans, selectedTransmissions) ? "#F0B64F" : "rgba(255,255,255,0.05)",
                    color: isSelected(trans, selectedTransmissions) ? "#000" : "rgba(255,255,255,0.7)",
                    border: "1px solid",
                    borderColor: isSelected(trans, selectedTransmissions) ? "#F0B64F" : "rgba(255,255,255,0.1)",
                    cursor: "pointer",
                    fontSize: "11px",
                    fontWeight: "600",
                  }}
                >
                  {trans.toUpperCase()}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Budget (AED) */}
        <div className="mb-3">
          <div
            className="d-flex align-items-center justify-content-between mb-2 cursor-pointer"
            onClick={() => toggleSection("budget")}
            style={{ cursor: "pointer" }}
          >
            <div className="d-flex align-items-center gap-2" style={{ fontWeight: "600", fontSize: "11px", color: "rgba(255,255,255,0.9)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              <Cash size={12} style={{ color: "#F0B64F" }} />
              <span>Budget (AED)</span>
            </div>
            {collapsedSections.budget ? <ChevronDown size={12} color="rgba(255,255,255,0.3)" /> : <ChevronUp size={12} color="rgba(255,255,255,0.3)" />}
          </div>
          {!collapsedSections.budget && (
            <div className="px-1">
              {/* Daily Price */}
              <div className="mb-4">
                <div className="d-flex justify-content-between mb-2">
                  <span style={{ fontSize: "10px", color: "#F0B64F", fontWeight: "600" }}>DAILY PRICE</span>
                  <span style={{ fontSize: "11px", color: "#fff", fontWeight: "700" }}>AED {Math.round(priceRange.min || filterCatalog?.minPrice || 0)} - {Math.round(priceRange.max || filterCatalog?.maxPrice || 0)}</span>
                </div>
                <div className="home-results-slider-wrapper">
                  <div className="home-results-slider-track" />
                  <div
                    className="home-results-slider-range"
                    style={{
                      left: `${(( (priceRange.min || filterCatalog?.minPrice || 0) - (filterCatalog?.minPrice || 0)) / ((filterCatalog?.maxPrice || 1) - (filterCatalog?.minPrice || 0) || 1)) * 100}%`,
                      right: `${100 - (( (priceRange.max || filterCatalog?.maxPrice || 0) - (filterCatalog?.minPrice || 0)) / ((filterCatalog?.maxPrice || 1) - (filterCatalog?.minPrice || 0) || 1)) * 100}%`
                    }}
                  />
                  <input
                    type="range"
                    min={filterCatalog?.minPrice || 0}
                    max={filterCatalog?.maxPrice || 0}
                    value={priceRange.min || filterCatalog?.minPrice || 0}
                    onChange={(e) => {
                      const val = Math.min(Number(e.target.value), (priceRange.max || filterCatalog?.maxPrice || 0) - 1);
                      handleDailyPriceChange(val, priceRange.max || filterCatalog?.maxPrice || 0);
                    }}
                    className="home-results-slider"
                  />
                  <input
                    type="range"
                    min={filterCatalog?.minPrice || 0}
                    max={filterCatalog?.maxPrice || 0}
                    value={priceRange.max || filterCatalog?.maxPrice || 0}
                    onChange={(e) => {
                      const val = Math.max(Number(e.target.value), (priceRange.min || filterCatalog?.minPrice || 0) + 1);
                      handleDailyPriceChange(priceRange.min || filterCatalog?.minPrice || 0, val);
                    }}
                    className="home-results-slider"
                  />
                </div>
                <div className="home-results-range-group mt-2">
                  <div className="home-results-range-box">
                    <label>MIN (AED)</label>
                    <input
                      type="text"
                      value={Math.round(priceRange.min || filterCatalog?.minPrice || 0)}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9]/g, "");
                        handleDailyPriceChange(val === "" ? 0 : Number(val), priceRange.max || filterCatalog?.maxPrice || 0);
                      }}
                    />
                  </div>
                  <div className="home-results-range-box">
                    <label>MAX (AED)</label>
                    <input
                      type="text"
                      value={Math.round(priceRange.max || filterCatalog?.maxPrice || 0)}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9]/g, "");
                        handleDailyPriceChange(priceRange.min || filterCatalog?.minPrice || 0, val === "" ? 0 : Number(val));
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Weekly Price */}
              <div className="mb-4">
                <div className="d-flex justify-content-between mb-2">
                  <span style={{ fontSize: "10px", color: "#F0B64F", fontWeight: "600" }}>WEEKLY PRICE</span>
                  <span style={{ fontSize: "11px", color: "#fff", fontWeight: "700" }}>AED {Math.round(weeklyPriceRange.min || filterCatalog?.minWeeklyPrice || 0)} - {Math.round(weeklyPriceRange.max || filterCatalog?.maxWeeklyPrice || 0)}</span>
                </div>
                <div className="home-results-slider-wrapper">
                  <div className="home-results-slider-track" />
                  <div
                    className="home-results-slider-range"
                    style={{
                      left: `${(( (weeklyPriceRange.min || filterCatalog?.minWeeklyPrice || 0) - (filterCatalog?.minWeeklyPrice || 0)) / ((filterCatalog?.maxWeeklyPrice || 1) - (filterCatalog?.minWeeklyPrice || 0) || 1)) * 100}%`,
                      right: `${100 - (( (weeklyPriceRange.max || filterCatalog?.maxWeeklyPrice || 0) - (filterCatalog?.minWeeklyPrice || 0)) / ((filterCatalog?.maxWeeklyPrice || 1) - (filterCatalog?.minWeeklyPrice || 0) || 1)) * 100}%`
                    }}
                  />
                  <input
                    type="range"
                    min={filterCatalog?.minWeeklyPrice || 0}
                    max={filterCatalog?.maxWeeklyPrice || 0}
                    value={weeklyPriceRange.min || filterCatalog?.minWeeklyPrice || 0}
                    onChange={(e) => {
                      const val = Math.min(Number(e.target.value), (weeklyPriceRange.max || filterCatalog?.maxWeeklyPrice || 0) - 1);
                      handleWeeklyPriceChange(val, weeklyPriceRange.max || filterCatalog?.maxWeeklyPrice || 0);
                    }}
                    className="home-results-slider"
                  />
                  <input
                    type="range"
                    min={filterCatalog?.minWeeklyPrice || 0}
                    max={filterCatalog?.maxWeeklyPrice || 0}
                    value={weeklyPriceRange.max || filterCatalog?.maxWeeklyPrice || 0}
                    onChange={(e) => {
                      const val = Math.max(Number(e.target.value), (weeklyPriceRange.min || filterCatalog?.minWeeklyPrice || 0) + 1);
                      handleWeeklyPriceChange(weeklyPriceRange.min || filterCatalog?.minWeeklyPrice || 0, val);
                    }}
                    className="home-results-slider"
                  />
                </div>
                <div className="home-results-range-group mt-2">
                  <div className="home-results-range-box">
                    <label>MIN (AED)</label>
                    <input
                      type="text"
                      value={Math.round(weeklyPriceRange.min || filterCatalog?.minWeeklyPrice || 0)}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9]/g, "");
                        handleWeeklyPriceChange(val === "" ? 0 : Number(val), weeklyPriceRange.max || filterCatalog?.maxWeeklyPrice || 0);
                      }}
                    />
                  </div>
                  <div className="home-results-range-box">
                    <label>MAX (AED)</label>
                    <input
                      type="text"
                      value={Math.round(weeklyPriceRange.max || filterCatalog?.maxWeeklyPrice || 0)}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9]/g, "");
                        handleWeeklyPriceChange(weeklyPriceRange.min || filterCatalog?.minWeeklyPrice || 0, val === "" ? 0 : Number(val));
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Monthly Price */}
              <div className="mb-4">
                <div className="d-flex justify-content-between mb-2">
                  <span style={{ fontSize: "10px", color: "#F0B64F", fontWeight: "600" }}>MONTHLY PRICE</span>
                  <span style={{ fontSize: "11px", color: "#fff", fontWeight: "700" }}>AED {Math.round(monthlyPriceRange.min || filterCatalog?.minMonthlyPrice || 0)} - {Math.round(monthlyPriceRange.max || filterCatalog?.maxMonthlyPrice || 0)}</span>
                </div>
                <div className="home-results-slider-wrapper">
                  <div className="home-results-slider-track" />
                  <div
                    className="home-results-slider-range"
                    style={{
                      left: `${(( (monthlyPriceRange.min || filterCatalog?.minMonthlyPrice || 0) - (filterCatalog?.minMonthlyPrice || 0)) / ((filterCatalog?.maxMonthlyPrice || 1) - (filterCatalog?.minMonthlyPrice || 0) || 1)) * 100}%`,
                      right: `${100 - (( (monthlyPriceRange.max || filterCatalog?.maxMonthlyPrice || 0) - (filterCatalog?.minMonthlyPrice || 0)) / ((filterCatalog?.maxMonthlyPrice || 1) - (filterCatalog?.minMonthlyPrice || 0) || 1)) * 100}%`
                    }}
                  />
                  <input
                    type="range"
                    min={filterCatalog?.minMonthlyPrice || 0}
                    max={filterCatalog?.maxMonthlyPrice || 0}
                    value={monthlyPriceRange.min || filterCatalog?.minMonthlyPrice || 0}
                    onChange={(e) => {
                      const val = Math.min(Number(e.target.value), (monthlyPriceRange.max || filterCatalog?.maxMonthlyPrice || 0) - 1);
                      handleMonthlyPriceChange(val, monthlyPriceRange.max || filterCatalog?.maxMonthlyPrice || 0);
                    }}
                    className="home-results-slider"
                  />
                  <input
                    type="range"
                    min={filterCatalog?.minMonthlyPrice || 0}
                    max={filterCatalog?.maxMonthlyPrice || 0}
                    value={monthlyPriceRange.max || filterCatalog?.maxMonthlyPrice || 0}
                    onChange={(e) => {
                      const val = Math.max(Number(e.target.value), (monthlyPriceRange.min || filterCatalog?.minMonthlyPrice || 0) + 1);
                      handleMonthlyPriceChange(monthlyPriceRange.min || filterCatalog?.minMonthlyPrice || 0, val);
                    }}
                    className="home-results-slider"
                  />
                </div>
                <div className="home-results-range-group mt-2">
                  <div className="home-results-range-box">
                    <label>MIN (AED)</label>
                    <input
                      type="text"
                      value={Math.round(monthlyPriceRange.min || filterCatalog?.minMonthlyPrice || 0)}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9]/g, "");
                        handleMonthlyPriceChange(val === "" ? 0 : Number(val), monthlyPriceRange.max || filterCatalog?.maxMonthlyPrice || 0);
                      }}
                    />
                  </div>
                  <div className="home-results-range-box">
                    <label>MAX (AED)</label>
                    <input
                      type="text"
                      value={Math.round(monthlyPriceRange.max || filterCatalog?.maxMonthlyPrice || 0)}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9]/g, "");
                        handleMonthlyPriceChange(monthlyPriceRange.min || filterCatalog?.minMonthlyPrice || 0, val === "" ? 0 : Number(val));
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Min Inputs (Combined or individual?) The user's snippet showed only one min input. 
                  I'll remove the manual min inputs to keep it clean and match the "one filter at a time" simplicity. */}
            </div>
          )}
        </div>

        {/* Mileage */}
        <div className="mb-2">
          <div
            className="d-flex align-items-center justify-content-between mb-2 cursor-pointer"
            onClick={() => toggleSection("mileage")}
            style={{ cursor: "pointer" }}
          >
            <div className="d-flex align-items-center gap-2" style={{ fontWeight: "600", fontSize: "11px", color: "rgba(255,255,255,0.9)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              <Speedometer size={12} style={{ color: "#F0B64F" }} />
              <span>Mileage</span>
            </div>
            {collapsedSections.mileage ? <ChevronDown size={12} color="rgba(255,255,255,0.3)" /> : <ChevronUp size={12} color="rgba(255,255,255,0.3)" />}
          </div>
          {!collapsedSections.mileage && (
            <div className="d-flex flex-column gap-2">
              <label className="d-flex align-items-center gap-2 cursor-pointer p-2 rounded-3" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}>
                <CustomCheckBox isChecked={isUnlimitedMileage} onChange={() => setIsUnlimitedMileage(!isUnlimitedMileage)} />
                <span style={{ fontSize: "12px", color: isUnlimitedMileage ? "#F0B64F" : "rgba(255,255,255,0.8)" }}>UNLIMITED MILEAGE</span>
              </label>
            </div>
          )}
        </div>
      </div>
    );
  }
  return (
    <div
      className={`modal ${isOpen ? "show" : ""}`}
      tabIndex={-1}
      style={{
        display: isOpen ? "flex" : "none",
        backgroundColor: "rgba(0,0,0,0.85)",
        backdropFilter: "blur(8px)",
        alignItems: "flex-end"
      }}
    >
      <style>{`
        .filter-modal-content {
          background: #111317 !important;
          border-radius: 30px 30px 0 0 !important;
          border: 1px solid rgba(255,255,255,0.08) !important;
          border-bottom: none !important;
          padding-bottom: env(safe-area-inset-bottom);
          max-height: 90vh;
          overflow-y: auto;
          width: 100%;
        }
        .filter-section-title {
          color: #F0B64F;
          font-size: 13px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 1.25rem;
        }
        .car-type-btn {
          background: #1a1d24;
          border: 1px solid rgba(255,255,255,0.15);
          color: #fff;
          border-radius: 50px;
          padding: 12px 20px;
          font-size: 13px;
          font-weight: 800;
          transition: all 0.2s ease;
          width: 48%;
          text-align: center;
          margin-bottom: 8px;
        }
        .car-type-btn.full-width {
          width: 100%;
        }
        .car-type-btn.active {
          border-color: #F0B64F;
          color: #F0B64F;
        }
        .filter-input-dark {
          background: #1a1d24 !important;
          border: 1px solid rgba(255,255,255,0.1) !important;
          border-radius: 12px !important;
          color: #fff !important;
          padding: 12px 16px !important;
          font-size: 15px !important;
          font-weight: 600 !important;
        }
        .apply-btn-container {
          position: sticky;
          bottom: 0;
          background: linear-gradient(to top, #111317 80%, transparent);
          padding: 1.5rem;
          margin-top: 1rem;
          z-index: 10;
        }
        .apply-cars-btn {
          background: #F0B64F;
          color: #000;
          border: none;
          border-radius: 50px;
          width: 100%;
          padding: 16px;
          font-size: 16px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          box-shadow: 0 10px 20px rgba(240, 182, 79, 0.3);
        }
      `}</style>

      <div className="modal-dialog m-0 w-100" style={{ maxWidth: "100%", marginTop: "auto" }}>
        <div className="modal-content filter-modal-content">
          <div className="d-flex align-items-center justify-content-between p-4 border-bottom border-secondary" style={{ borderColor: "rgba(255,255,255,0.05) !important" }}>
            <h5 className="mb-0 fw-bold text-white" style={{ letterSpacing: "1px" }}>FILTERS</h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>

          <div className="modal-body p-4">
            <div className="mb-5">
              <div className="filter-section-title">Daily Price Range (AED)</div>
              <div className="px-2 mb-3">
                <div className="home-results-slider-wrapper">
                  <div className="home-results-slider-track" />
                  <div
                    className="home-results-slider-range"
                    style={{
                      left: `${((priceRange.min - (filterCatalog?.minPrice || 0)) / ((filterCatalog?.maxPrice || 1) - (filterCatalog?.minPrice || 0) || 1)) * 100}%`,
                      right: `${100 - ((priceRange.max - (filterCatalog?.minPrice || 0)) / ((filterCatalog?.maxPrice || 1) - (filterCatalog?.minPrice || 0) || 1)) * 100}%`
                    }}
                  />
                  <input
                    type="range"
                    min={filterCatalog?.minPrice || 0}
                    max={filterCatalog?.maxPrice || 10000}
                    value={priceRange.min}
                    onChange={(e) => {
                      const val = Math.min(Number(e.target.value), priceRange.max - 1);
                      handleDailyPriceChange(val, priceRange.max);
                    }}
                    className="home-results-slider"
                  />
                  <input
                    type="range"
                    min={filterCatalog?.minPrice || 0}
                    max={filterCatalog?.maxPrice || 10000}
                    value={priceRange.max}
                    onChange={(e) => {
                      const val = Math.max(Number(e.target.value), priceRange.min + 1);
                      handleDailyPriceChange(priceRange.min, val);
                    }}
                    className="home-results-slider"
                  />
                </div>
              </div>
              <div className="d-flex justify-content-between px-2 small fw-bold">
                <span className="text-secondary">Min: {priceRange.min}</span>
                <span className="text-warning">Max: {priceRange.max || filterCatalog?.maxPrice || 0}</span>
              </div>
            </div>

            <div className="mb-5">
              <div className="filter-section-title">Weekly Price Range (AED)</div>
              <div className="px-2 mb-3">
                <div className="home-results-slider-wrapper">
                  <div className="home-results-slider-track" />
                  <div
                    className="home-results-slider-range"
                    style={{
                      left: `${((weeklyPriceRange.min - (filterCatalog?.minWeeklyPrice || 0)) / ((filterCatalog?.maxWeeklyPrice || 1) - (filterCatalog?.minWeeklyPrice || 0) || 1)) * 100}%`,
                      right: `${100 - ((weeklyPriceRange.max - (filterCatalog?.minWeeklyPrice || 0)) / ((filterCatalog?.maxWeeklyPrice || 1) - (filterCatalog?.minWeeklyPrice || 0) || 1)) * 100}%`
                    }}
                  />
                  <input
                    type="range"
                    min={filterCatalog?.minWeeklyPrice || 0}
                    max={filterCatalog?.maxWeeklyPrice || 10000}
                    value={weeklyPriceRange.min}
                    onChange={(e) => {
                      const val = Math.min(Number(e.target.value), weeklyPriceRange.max - 1);
                      handleWeeklyPriceChange(val, weeklyPriceRange.max);
                    }}
                    className="home-results-slider"
                  />
                  <input
                    type="range"
                    min={filterCatalog?.minWeeklyPrice || 0}
                    max={filterCatalog?.maxWeeklyPrice || 10000}
                    value={weeklyPriceRange.max}
                    onChange={(e) => {
                      const val = Math.max(Number(e.target.value), weeklyPriceRange.min + 1);
                      handleWeeklyPriceChange(weeklyPriceRange.min, val);
                    }}
                    className="home-results-slider"
                  />
                </div>
              </div>
              <div className="d-flex justify-content-between px-2 small fw-bold">
                <span className="text-secondary">Min: {weeklyPriceRange.min}</span>
                <span className="text-warning">Max: {weeklyPriceRange.max || filterCatalog?.maxWeeklyPrice || 0}</span>
              </div>
            </div>

            <div className="mb-5 border-bottom border-dark pb-4">
              <div className="filter-section-title">Monthly Price Range (AED)</div>
              <div className="px-2 mb-3">
                <div className="home-results-slider-wrapper">
                  <div className="home-results-slider-track" />
                  <div
                    className="home-results-slider-range"
                    style={{
                      left: `${((monthlyPriceRange.min - (filterCatalog?.minMonthlyPrice || 0)) / ((filterCatalog?.maxMonthlyPrice || 1) - (filterCatalog?.minMonthlyPrice || 0) || 1)) * 100}%`,
                      right: `${100 - ((monthlyPriceRange.max - (filterCatalog?.minMonthlyPrice || 0)) / ((filterCatalog?.maxMonthlyPrice || 1) - (filterCatalog?.minMonthlyPrice || 0) || 1)) * 100}%`
                    }}
                  />
                  <input
                    type="range"
                    min={filterCatalog?.minMonthlyPrice || 0}
                    max={filterCatalog?.maxMonthlyPrice || 10000}
                    value={monthlyPriceRange.min}
                    onChange={(e) => {
                      const val = Math.min(Number(e.target.value), monthlyPriceRange.max - 1);
                      handleMonthlyPriceChange(val, monthlyPriceRange.max);
                    }}
                    className="home-results-slider"
                  />
                  <input
                    type="range"
                    min={filterCatalog?.minMonthlyPrice || 0}
                    max={filterCatalog?.maxMonthlyPrice || 10000}
                    value={monthlyPriceRange.max}
                    onChange={(e) => {
                      const val = Math.max(Number(e.target.value), monthlyPriceRange.min + 1);
                      handleMonthlyPriceChange(monthlyPriceRange.min, val);
                    }}
                    className="home-results-slider"
                  />
                </div>
              </div>
              <div className="d-flex justify-content-between px-2 small fw-bold">
                <span className="text-secondary">Min: {monthlyPriceRange.min}</span>
                <span className="text-warning">Max: {monthlyPriceRange.max || filterCatalog?.maxMonthlyPrice || 0}</span>
              </div>
            </div>

            {/* Car Types */}
            <div className="mb-5 pb-3 border-bottom border-dark">
              <div className="filter-section-title">Car Type</div>
              <div className="d-flex flex-wrap justify-content-between">
                {(filterCatalog?.carTypes || []).map((type: string, idx: number) => (
                  <div
                    key={type}
                    className={`car-type-btn ${selectedCarTypes.includes(type) ? "active" : ""} ${idx === 2 ? "full-width" : ""}`}
                    onClick={() => toggleValue(type, setSelectedCarTypes)}
                  >
                    {type.toUpperCase()}
                  </div>
                ))}
              </div>
            </div>

            {/* Brands */}
            <div className="mb-5">
              <div className="filter-section-title">Brands</div>
              <div className="row g-2 custom-scrollbar" style={{ maxHeight: "250px", overflowY: "auto" }}>
                {(filterCatalog?.brands || []).map((brand: string) => (
                  <div className="col-12" key={brand}>
                    <div
                      className="d-flex align-items-center gap-3 p-2 rounded-3"
                      onClick={() => toggleValue(brand, setSelectedBrands)}
                      style={{ cursor: "pointer", background: selectedBrands.includes(brand) ? "rgba(255,255,255,0.03)" : "transparent" }}
                    >
                      <CustomCheckBox isChecked={selectedBrands.includes(brand)} onChange={() => { }} />
                      <span style={{ color: selectedBrands.includes(brand) ? "#F0B64F" : "rgba(255,255,255,0.7)", fontWeight: "600", fontSize: "14px" }}>
                        {brand.toUpperCase()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Transmission & Fuel */}
            <div className="row mb-5">
              <div className="col-6">
                <div className="filter-section-title">Transmission</div>
                <div className="d-flex flex-column gap-2">
                  {(filterCatalog?.transmissions || []).map((t: string) => (
                    <div key={t} className="d-flex align-items-center gap-2" onClick={() => toggleValue(t, setSelectedTransmissions)}>
                      <CustomCheckBox isChecked={selectedTransmissions.includes(t)} onChange={() => { }} />
                      <span className="small text-white-50">{t.toUpperCase()}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="col-6">
                <div className="filter-section-title">Fuel Type</div>
                <div className="d-flex flex-column gap-2">
                  {(filterCatalog?.fuelTypes || []).map((f: string) => (
                    <div key={f} className="d-flex align-items-center gap-2" onClick={() => toggleValue(f, setSelectedFuelTypes)}>
                      <CustomCheckBox isChecked={selectedFuelTypes.includes(f)} onChange={() => { }} />
                      <span className="small text-white-50">{f.toUpperCase()}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Sticky Footer Apply Button */}
          <div className="apply-btn-container">
            <button className="apply-cars-btn" onClick={handleProceedToCustomize}>
              APPLY {getFilteredCount()} CARS
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerCustomizeYourSearch;
